import zustand, { type State } from 'zustand'
import { uploadSlide, uploadHandout } from './api/upload'

type TrayStoreVideoIntention = {
  type: 'video'
  action: 'add' | 'edit'
  currentCardId?: string
}

type TrayStoreSlideIntention = {
  type: 'slide'
  action: 'add'
  currentCardId: string
}

type TrayStoreNoteIntention = {
  type: 'note'
  action: 'edit'
  cardId: string
}

type TrayStoreComingUpIntention = {
  type: 'coming-up'
  action: 'edit'
  cardId: string
}

type TrayStoreIntention =
  | TrayStoreVideoIntention
  | TrayStoreSlideIntention
  | TrayStoreNoteIntention
  | TrayStoreComingUpIntention

interface TrayStore extends State {
  currentlyEditing: string | null
  setCurrentlyEditing: (cardId: string) => void
  clearCurrentlyEditing: () => void
  intention?: TrayStoreIntention
  setAddVideoIntention: (currentCardId?: string) => void
  setEditVideoIntention: (currentCardId: string) => void
  setEditNoteIntention: (cardId: string) => void
  setEditComingUpIntention: (cardId: string) => void
  clearIntention: () => void
  startUploadSlide: (id: string, file: File) => Promise<string[]>
  startUploadHandout: (
    id: string,
    file: File
  ) => Promise<{
    filename: string
    fileSize: number
    iconName:
      | 'audio'
      | 'video'
      | 'presentation'
      | 'image'
      | 'document'
      | 'generic'
  }>
  handoutUpload: {
    inProgress: boolean
  }
  slideUpload: {
    inProgress: boolean
  }
}

const useTrayStore = zustand<TrayStore>((set) => {
  const setCurrentlyEditing = (cardId: string) => {
    set({ currentlyEditing: cardId })
  }
  const clearCurrentlyEditing = () => {
    set({ currentlyEditing: null })
  }
  const setAddVideoIntention = (currentCardId?: string) => {
    set({ intention: { type: 'video', action: 'add', currentCardId } })
  }
  const setEditVideoIntention = (currentCardId: string) => {
    set({ intention: { type: 'video', action: 'edit', currentCardId } })
  }
  const setEditNoteIntention = (cardId: string) => {
    set({ intention: { type: 'note', action: 'edit', cardId } })
  }
  const setEditComingUpIntention = (cardId: string) => {
    set({ intention: { type: 'coming-up', action: 'edit', cardId } })
  }
  const startUploadSlide = async (
    liveId: string,
    file: File
  ): Promise<string[]> => {
    set({ slideUpload: { inProgress: true } })
    const res = await uploadSlide(liveId, file)
    set({ slideUpload: { inProgress: false } })
    return res.map((slide) => slide.url)
  }
  const startUploadHandout = async (
    liveId: string,
    file: File
  ): Promise<{
    filename: string
    fileSize: number
    iconName:
      | 'audio'
      | 'video'
      | 'presentation'
      | 'image'
      | 'document'
      | 'generic'
  }> => {
    set({ handoutUpload: { inProgress: true } })
    const {
      filename,
      size: fileSize,
      icon_name: iconName,
    } = await uploadHandout(liveId, file)
    set({ handoutUpload: { inProgress: false } })
    return {
      filename,
      fileSize,
      iconName,
    }
  }

  const clearIntention = () => {
    set({ intention: undefined })
  }
  return {
    currentlyEditing: null,
    setCurrentlyEditing,
    clearCurrentlyEditing,
    intention: undefined,
    setAddVideoIntention,
    setEditVideoIntention,
    setEditNoteIntention,
    setEditComingUpIntention,
    clearIntention,
    startUploadSlide,
    startUploadHandout,
    slideUpload: { inProgress: false },
    handoutUpload: { inProgress: false },
  }
})

export default useTrayStore
